/* eslint-disable no-unused-vars,prefer-const,no-template-curly-in-string,no-undef */
// This mixin is meant to be included with every single page component in src/pages/{page}/{page}.vue
import Cookies from 'js-cookie';
import fragmentService from '../services/fragment.service';
import profileService from '../services/profile.service';
import messageService from '../services/message.service';
import { getDomainName } from '@/util/url';

let messageIntervalId;
let headerHtmlTemplate = '';
let headerHtml = '';
let footerHtml = '';
let menuHtml = '';
let profileFragments = {};
let unreadMessageCount = 0;

const FOOTER_HEIGHT = 176; // this might need changing. The reason it was set to a const, was because
// the footer height was flickering when content loaded above it, causing issues with stickying it
// at the bottom of the page

function loadAssets() {
  return new Promise((resolve, reject) => {
    fragmentService
      .getFragments()
      .then((res) => {
        if (!res?.data) {
          reject();
        }
        const script = document.createElement('script');
        script.setAttribute('src', res.data.js);
        document.body.appendChild(script);

        const link = document.createElement('link');
        link.setAttribute('rel', 'stylesheet');
        link.setAttribute('href', res.data.css);
        document.head.appendChild(link);
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
}

function stickyFooter() {
  const bodyHeight = document.body.clientHeight;
  const headerHeight = document.querySelector('.header-js').clientHeight || 0;
  const underHeaderHeight = document.querySelector('.under-header-js').clientHeight || 0;
  const footer = document.querySelector('.footer-js');
  if (footer) {
    footer.style.minHeight = `${FOOTER_HEIGHT}px`;
  }
  const main = document.querySelector('.main-js');

  if (main) {
    main.style.minHeight = `${bodyHeight - headerHeight - underHeaderHeight - FOOTER_HEIGHT}px`;
  }
}

const pageMixin = {
  data() {
    return {
      currentUserProfile: null,
      profileId: null,
      isEmployeeInfoDisplayed: true,
      isProfileInfoDisplayed: true,
      fragmentsLoaded: false,
      importantMessages: [],
      displayedImportantMessage: null,
      loadingImportantMessage: false,
      homeHref: '/',
    };
  },

  created() {
    profileService.getCurrentUser().then((res) => {
      this.currentUserProfile = res.data;
      this.isEmployeeInfoDisplayed = Object.keys(this.currentUserProfile.employee).length > 0;
      this.isProfileInfoDisplayed = !this.currentUserProfile.employee.institution || Object.keys(this.currentUserProfile.employee.institution).length === 0;
      if (!res.data?.address) {
        this.currentUserProfile.address = {};
      }
      if (!res.data?.employee) {
        this.currentUserProfile.employee = {};
      }
      if (!res.data?.employee?.institution) {
        this.currentUserProfile.employee.institution = {};
      }
      if (!res.data?.employee?.institution?.address) {
        this.currentUserProfile.employee.institution.address = {};
      }
    });
  },

  beforeDestroy() {
    clearInterval(messageIntervalId);
  },

  methods: {
    async setupPage() {
      clearInterval(messageIntervalId);

      // fetch fragment HTML
      const fragmentRequests = [
        fragmentService.getHeader().then(({ data }) => {
          headerHtmlTemplate = data;
        }),
        fragmentService.getFooter().then(({ data }) => {
          footerHtml = data;
        }),
        loadAssets().then(() => {}),
        fragmentService.getMenu().then(({ data }) => {
          menuHtml = data;
        }),
      ];
      const token = this.$keycloak?.token || Cookies.getJSON('accessToken');
      if (token) {
        // if the user is signed in
        this.setupUnreadMessageCountFetching();
        fragmentRequests.push(
          fragmentService.getProfile().then(({ data }) => {
            profileFragments = data;
            this.profileId = Number.parseInt(data.profileId, 10);
          })
        );
        fragmentRequests.push(
          fragmentService.getUnreadMessageCount().then(({ data }) => {
            unreadMessageCount = data.unreadMessageCount;
            if (data.importantMessageCount > 0) {
              this.fetchImportantMessages();
            }
          })
        );
      }
      await Promise.allSettled(fragmentRequests);

      // build fragment HTML
      const underHeaderBlock = document.querySelector('.under-header-js');
      if (underHeaderBlock) {
        underHeaderBlock.remove();
      }
      const questionBlock = document.getElementById('question_block');
      if (questionBlock) {
        questionBlock.remove();
      }
      headerHtml = this.putProfileFragmentsToHeader(headerHtmlTemplate);
      document.querySelector('.header-js').outerHTML = headerHtml;
      document.querySelector('.footer-js').outerHTML = footerHtml;
      document.querySelector('.main-js nav').outerHTML = menuHtml;

      // setup language switching
      const languageSwitcher = document.querySelector('.eptp_under_header__language');
      if (languageSwitcher) {
        languageSwitcher.onclick = (event) => {
          event.preventDefault();
          if (languageSwitcher.innerHTML.indexOf('EN') > -1) {
            Cookies.set('locale', 'en', {
              domain: getDomainName(),
            });
          } else {
            Cookies.set('locale', 'lt', {
              domain: getDomainName(),
            });
          }
          window.location.reload();
        };
      }

      this.fragmentsLoaded = true;

      await this.$nextTick();
      window.removeEventListener('resize', stickyFooter);
      window.addEventListener('resize', stickyFooter);
      stickyFooter();
    },

    setupUnreadMessageCountFetching() {
      messageIntervalId = setInterval(() => {
        if (this.importantMessages.length) {
          return;
        }
        fragmentService.getUnreadMessageCount().then((res) => {
          const nodes = document.querySelectorAll(
            '.eptp_user_nav__links__number, .eptp_user_toggle__number'
          );
          for (let i = 0; i < nodes.length; i += 1) {
            nodes[i].textContent = res.data.unreadMessageCount;
          }
        });
      }, 30000);
    },

    markImportantMessageAsRead(message) {
      if (this.loadingImportantMessage) {
        return;
      }
      messageService
        .markAsRead([message.id])
        .then(() => {
          if (this.$root) {
            this.$root.$emit('important-message-read');
          }
          this.displayNextImportantMessage();
        });
    },

    putProfileFragmentsToHeader(html) {
      // eslint-disable-next-line no-template-curly-in-string
      return html
        .replace(/\${firstName}/g, profileFragments.firstName)
        .replace(/\${lastName}/g, profileFragments.lastName)
        .replace(/\${nameShort}/g, profileFragments.nameFull)
        .replace(/\${unreadMessageCount}/g, unreadMessageCount)
        .replace(/\${profileUrl}/g, profileFragments.profileUrl)
        .replace(/\${profileListUrl}/g, profileFragments.profileListUrl)
        .replace(/\${changeProfileUrl}/g, profileFragments.changeProfileUrl)
        .replace(/\${logoutUrl}/g, profileFragments.logoutUrl)
        .replace(/\${changeLanguageUrl_LT}/g, '#')
        .replace(/\${changeLanguageUrl_EN}/g, '#');
    },

    fetchImportantMessages() {
      messageService.getImportantList().then((res) => {
        this.importantMessages = res.data.message;
        if (this.importantMessages.length) {
          this.displayNextImportantMessage();
        }
      });
    },

    displayNextImportantMessage() {
      if (!this.importantMessages.length) {
        this.displayedImportantMessage = null;
        return;
      }
      this.loadingImportantMessage = true;
      messageService
        .getById(this.importantMessages[0].id)
        .then((res) => {
          this.importantMessages = this.importantMessages.filter((m) => m.id !== res.data.id);
          this.displayedImportantMessage = res.data;
          const modal = document.querySelector('.modal');
          if (modal) {
            modal.scrollTo(0, 0);
          }
        })
        .finally(() => {
          this.loadingImportantMessage = false;
        });
    },
  },
};

export default pageMixin;
